import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Mail } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";

export default function Privacy() {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="privacy-page"
    >
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-10 md:pb-14">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="privacy-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur"
          >
            <ShieldCheck className="w-3 h-3 text-[#C9A349]" />
            NZ Privacy Act 2020 · NZ Taxation Limited
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]"
            data-testid="privacy-headline"
          >
            Privacy <span className="text-[#C9A349]">Policy.</span>
          </motion.h1>

          <p
            className="mt-5 text-sm text-slate-500 uppercase tracking-[0.18em] font-bold"
            data-testid="privacy-updated"
          >
            Last updated: 25 May 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white border-b border-slate-200">
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <Lede>
            NZ Taxation Limited (<strong>"we"</strong>, <strong>"us"</strong>,{" "}
            <strong>"our"</strong>) is committed to protecting the privacy of our clients and
            visitors in accordance with the Privacy Act 2020 (New Zealand).
          </Lede>

          <H2 n={1}>Who we are</H2>
          <P>
            NZ Taxation Limited is a New Zealand-based accounting and tax practice. We can be
            contacted at:
          </P>
          <BulletList>
            <li>
              Email:{" "}
              <a
                href="mailto:info@nztaxation.nz"
                className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
              >
                info@nztaxation.nz
              </a>
            </li>
            <li>
              Website:{" "}
              <a
                href="https://www.nztaxation.nz"
                className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
              >
                https://www.nztaxation.nz
              </a>
            </li>
          </BulletList>

          <H2 n={2}>Information we collect</H2>
          <P>
            We collect personal information necessary to provide our tax, accounting and
            advisory services, including:
          </P>
          <BulletList>
            <li>Name, contact details, and IRD number</li>
            <li>Income, expense, and financial records</li>
            <li>Bank account details (where required for tax refunds or invoicing)</li>
            <li>Communications between you and our team</li>
            <li>
              Documents you provide for the preparation of tax returns and financial statements
            </li>
          </BulletList>

          <H2 n={3}>How we collect it</H2>
          <P>
            We collect information directly from you (via email, our onboarding form, or in
            person) and from authorised third parties such as Inland Revenue (IRD), Xero, Xero
            Practice Manager, and other systems you have authorised us to access on your behalf.
          </P>

          <H2 n={4}>How we use your information</H2>
          <P>We use your information only to:</P>
          <BulletList>
            <li>Prepare and lodge your tax returns and financial statements</li>
            <li>Communicate with you about your account and our services</li>
            <li>Comply with our legal and regulatory obligations (Inland Revenue, AML/CFT)</li>
            <li>Improve the security and quality of our internal systems</li>
          </BulletList>
          <P>
            We do not sell your information to third parties. We do not send marketing emails
            unrelated to our services.
          </P>

          <H2 n={5}>Who we share it with</H2>
          <P>
            We only share your information where necessary to provide our services or where
            required by law, including:
          </P>
          <BulletList>
            <li>Inland Revenue for lodgement and compliance</li>
            <li>Xero Limited and Xero Practice Manager as part of our practice management workflows</li>
            <li>
              Our authorised software providers (e.g. e-signing, secure cloud storage), all
              bound by confidentiality and equivalent privacy standards
            </li>
            <li>Government authorities where legally compelled to do so</li>
          </BulletList>

          <H2 n={6}>How we store and protect it</H2>
          <P>
            Your information is stored in secure systems hosted in New Zealand or in
            jurisdictions with equivalent privacy protections. Access is restricted to
            authorised staff. We use encryption in transit (HTTPS) and at rest, multi-factor
            authentication on staff accounts, and audit logging on systems that handle client
            data.
          </P>

          <H2 n={7}>How long we keep it</H2>
          <P>
            We retain your records for at least seven (7) years as required by Inland Revenue,
            and longer where ongoing legal or business needs apply. After that, records are
            securely destroyed.
          </P>

          <H2 n={8}>Your rights</H2>
          <P>Under the Privacy Act 2020, you have the right to:</P>
          <BulletList>
            <li>Access the personal information we hold about you</li>
            <li>Correct anything that is inaccurate</li>
            <li>Request deletion of information that we are no longer required to retain</li>
            <li>Complain if you believe your privacy has been breached</li>
          </BulletList>
          <P>
            To exercise any of these rights, please email{" "}
            <a
              href="mailto:info@nztaxation.nz"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              info@nztaxation.nz
            </a>
            . We will respond within 20 working days.
          </P>
          <P>
            If you are not satisfied with our response, you may contact the Office of the
            Privacy Commissioner at{" "}
            <a
              href="https://www.privacy.org.nz"
              target="_blank"
              rel="noreferrer"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              https://www.privacy.org.nz
            </a>
            .
          </P>

          <H2 n={9}>Cookies and website analytics</H2>
          <P>
            Our website uses basic cookies and analytics tools to understand how visitors use
            the site. No personally identifiable information is collected through these
            cookies unless you choose to submit it to us via a form.
          </P>

          <H2 n={10}>Changes to this policy</H2>
          <P>
            We may update this policy from time to time. The latest version will always be
            published on our website with the date of the most recent update shown above.
          </P>

          <H2 n={11}>Contact us</H2>
          <P>
            For any questions about this Privacy Policy or how we handle your information,
            please contact:
          </P>

          <div className="mt-6 border border-slate-200 bg-slate-50 p-6 md:p-7">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
              NZ Taxation Limited
            </div>
            <div className="mt-2 font-heading font-black text-xl text-[#0A192F]">
              Michael Amador
            </div>
            <div className="text-sm text-slate-600">Director</div>
            <a
              href="mailto:info@nztaxation.nz"
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#0A192F] hover:text-[#C9A349] transition-colors"
              data-testid="privacy-contact-email"
            >
              <Mail className="w-4 h-4" /> info@nztaxation.nz
            </a>
          </div>
        </article>
      </section>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

/* ---------- Building blocks ---------- */

function H2({ n, children }) {
  return (
    <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-12 mb-3 text-[#0A192F] leading-tight scroll-mt-24">
      <span className="text-[#C9A349]">{String(n).padStart(2, "0")}.</span> {children}
    </h2>
  );
}

function P({ children }) {
  return <p className="text-base md:text-[17px] leading-[1.75] text-slate-700 my-4">{children}</p>;
}

function BulletList({ children }) {
  return (
    <ul className="list-none p-0 my-4 space-y-2.5 text-base md:text-[17px] text-slate-700">
      {Array.isArray(children) || children?.type === "li"
        ? wrapItems(children)
        : children}
    </ul>
  );
}

function wrapItems(items) {
  const arr = Array.isArray(items) ? items : [items];
  return arr.map((item, i) => (
    <li key={i} className="flex items-start gap-3 leading-relaxed">
      <span className="w-1.5 h-1.5 bg-[#C9A349] flex-shrink-0 mt-2.5" />
      <span>{item.props ? item.props.children : item}</span>
    </li>
  ));
}

function Lede({ children }) {
  return (
    <div className="border-l-4 border-[#C9A349] bg-[#FAF6EC] px-5 md:px-7 py-5 md:py-6 text-base md:text-lg text-slate-700 leading-relaxed">
      {children}
    </div>
  );
}
